/* Global Quick View controller -- window.BeepQuickView.
   Any element carrying [data-quick-view-trigger][data-quick-view-handle]
   opens this modal (event delegation, so it works for cards rendered
   after page load too -- collection "load more", client-rendered
   recommendation sliders, etc). Fetches the real product via Shopify's
   /products/{handle}.js endpoint -- same real data the PDP itself uses,
   just fewer of the trust/marketing rows so it stays compact. Add to
   cart reuses window.BeepCart.add() so the header badge/toast/drawer
   stay in sync with anything added from here.

   Image gallery: a real slider -- prev/next arrow buttons, touch swipe
   on mobile, left/right arrow keys, and a quick crossfade between
   images -- not just click-a-thumbnail. All product images are
   preloaded (full size) the instant the modal opens (no loading="lazy"
   on the small thumbnails either) so switching images feels instant
   instead of waiting on a fresh network fetch per click. Thumbnail row
   scrolls horizontally on narrow screens instead of overflowing/
   clipping the panel. */
window.BeepQuickView = (function () {
  'use strict';

  var modal, body;
  var isOpen = false;
  var scrollLocked = false;
  var requestToken = 0;
  var navigate = null; // set per-render by wireInteractions; used by the global keydown handler

  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function rootPath() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (r.slice(-1) !== '/') r += '/';
    return r;
  }

  function escapeHtml(s) {
    return (s || '').replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function formatMoney(cents) {
    var amount = (cents || 0) / 100;
    try {
      var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: currency }).format(amount);
    } catch (e) {
      return '$' + amount.toFixed(2);
    }
  }

  function fixImg(url, width) {
    if (!url) return '';
    var u = url.indexOf('//') === 0 ? 'https:' + url : url;
    return u.replace(/(\.[a-zA-Z0-9]+)(\?|$)/, '_' + width + 'x' + width + '$1$2');
  }

  function preloadImages(urls) {
    urls.forEach(function (src) {
      var img = new Image();
      img.src = src;
    });
  }

  function lockScroll() {
    if (scrollLocked) return;
    scrollLocked = true;
    var scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.classList.add('quick-view-locked');
    if (scrollBarWidth > 0) document.body.style.paddingRight = scrollBarWidth + 'px';
  }
  function unlockScroll() {
    if (!scrollLocked) return;
    scrollLocked = false;
    document.body.classList.remove('quick-view-locked');
    document.body.style.paddingRight = '';
  }

  function close() {
    if (!modal) return;
    isOpen = false;
    navigate = null;
    modal.classList.remove('is-open');
    unlockScroll();
    setTimeout(function () { if (!isOpen) modal.hidden = true; }, 220);
  }

  function renderPrice(variant) {
    var price = formatMoney(variant.price);
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      return '<span class="quick-view__price-sale">' + price + '</span><span class="quick-view__price-compare">' + formatMoney(variant.compare_at_price) + '</span>';
    }
    return '<span>' + price + '</span>';
  }

  function renderStock(variant) {
    if (!variant || variant.available === false) {
      return '<span class="quick-view__stock-icon quick-view__stock-icon--out">&times;</span>Out of stock';
    }
    return '<span class="quick-view__stock-icon">&#10003;</span>In stock, ready to ship';
  }

  function renderError() {
    body.innerHTML = '<div class="quick-view__error-state"><p>Sorry, we couldn&rsquo;t load this product.</p></div>';
  }

  var arrowSvg = {
    prev: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6"/></svg>',
    next: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 18l6-6-6-6"/></svg>'
  };

  function renderProduct(product) {
    var rawImages = (product.images && product.images.length) ? product.images : (product.featured_image ? [product.featured_image] : []);
    var images = rawImages.map(function (u) { return fixImg(u, 900); });
    var thumbSrcs = rawImages.slice(0, 5).map(function (u) { return fixImg(u, 150); });
    var hasMultiple = images.length > 1;

    var thumbs = hasMultiple
      ? images.slice(0, 5).map(function (img, i) {
          return '<button type="button" class="quick-view__thumb' + (i === 0 ? ' is-active' : '') + '" data-qv-thumb data-index="' + i + '"><img src="' + thumbSrcs[i] + '" alt=""></button>';
        }).join('')
      : '';

    var variants = product.variants || [];
    var multi = variants.length > 1;
    var selectedVariant = variants[0] || {};
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].available) { selectedVariant = variants[i]; break; }
    }

    var variantField = multi
      ? '<div class="quick-view__field">' +
          '<label for="QuickViewVariant">Options</label>' +
          '<select id="QuickViewVariant" data-qv-variant>' +
          variants.map(function (v) {
            return '<option value="' + v.id + '" data-price="' + v.price + '" data-available="' + v.available + '" data-compare="' + (v.compare_at_price || 0) + '"' + (v.id === selectedVariant.id ? ' selected' : '') + (!v.available ? ' disabled' : '') + '>' + escapeHtml(v.title) + (v.available ? '' : ' - Sold out') + '</option>';
          }).join('') +
          '</select>' +
        '</div>'
      : '';

    body.innerHTML =
      '<div class="quick-view__media">' +
        '<div class="quick-view__media-main" data-qv-media-main>' +
          (hasMultiple ? '<button type="button" class="quick-view__nav quick-view__nav--prev" data-qv-prev aria-label="Previous image">' + arrowSvg.prev + '</button>' : '') +
          '<img data-qv-main-img src="' + (images[0] || '') + '" alt="' + escapeHtml(product.title) + '">' +
          (hasMultiple ? '<button type="button" class="quick-view__nav quick-view__nav--next" data-qv-next aria-label="Next image">' + arrowSvg.next + '</button>' : '') +
          (hasMultiple ? '<span class="quick-view__counter" data-qv-counter>1 / ' + images.length + '</span>' : '') +
        '</div>' +
        (thumbs ? '<div class="quick-view__thumbs">' + thumbs + '</div>' : '') +
      '</div>' +
      '<div class="quick-view__info">' +
        (product.vendor ? '<p class="quick-view__vendor">' + escapeHtml(product.vendor) + '</p>' : '') +
        '<h2 class="quick-view__title">' + escapeHtml(product.title) + '</h2>' +
        '<div class="quick-view__price-row" data-qv-price>' + renderPrice(selectedVariant) + '</div>' +
        '<div class="quick-view__stock" data-qv-stock>' + renderStock(selectedVariant) + '</div>' +
        '<form class="quick-view__form" data-qv-form>' +
          variantField +
          '<div class="quick-view__qty-row">' +
            '<span class="quick-view__qty-label">Quantity</span>' +
            '<div class="quick-view__qty">' +
              '<button type="button" data-qv-qty-minus aria-label="Decrease quantity">&minus;</button>' +
              '<input type="number" min="1" value="1" data-qv-qty-input inputmode="numeric">' +
              '<button type="button" data-qv-qty-plus aria-label="Increase quantity">+</button>' +
            '</div>' +
          '</div>' +
          '<button type="submit" class="quick-view__add" data-qv-add' + (selectedVariant.available ? '' : ' disabled') + '>' + (selectedVariant.available ? 'Add to cart' : 'Sold out') + '</button>' +
          '<p class="quick-view__error" data-qv-error hidden></p>' +
        '</form>' +
        '<a href="' + product.url + '" class="quick-view__full-link">View full details<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg></a>' +
      '</div>';

    wireInteractions(variants, images);
    if (images.length) preloadImages(images);
  }

  function wireInteractions(variants, images) {
    var mainImgEl = qs('[data-qv-main-img]', body);
    var mediaMain = qs('[data-qv-media-main]', body);
    var thumbButtons = qsa('[data-qv-thumb]', body);
    var counterEl = qs('[data-qv-counter]', body);
    var currentIndex = 0;

    function showImage(i) {
      if (!images || images.length < 2) return;
      currentIndex = ((i % images.length) + images.length) % images.length;
      if (mainImgEl) {
        mainImgEl.classList.add('is-swapping');
        setTimeout(function () {
          mainImgEl.src = images[currentIndex];
          mainImgEl.classList.remove('is-swapping');
        }, 90);
      }
      thumbButtons.forEach(function (b, idx) { b.classList.toggle('is-active', idx === currentIndex); });
      if (counterEl) counterEl.textContent = (currentIndex + 1) + ' / ' + images.length;
    }

    thumbButtons.forEach(function (btn, idx) {
      btn.addEventListener('click', function () { showImage(idx); });
    });

    var prevBtn = qs('[data-qv-prev]', body);
    var nextBtn = qs('[data-qv-next]', body);
    if (prevBtn) prevBtn.addEventListener('click', function () { showImage(currentIndex - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { showImage(currentIndex + 1); });

    navigate = function (dir) { showImage(currentIndex + dir); };

    /* Touch swipe on the main image, mobile-friendly (pan-y lets the
       modal itself still scroll vertically while horizontal swipes are
       captured for image navigation). */
    if (mediaMain && images && images.length > 1) {
      var touchStartX = 0, touchStartY = 0, touching = false;
      mediaMain.addEventListener('touchstart', function (e) {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        touching = true;
      }, { passive: true });
      mediaMain.addEventListener('touchend', function (e) {
        if (!touching) return;
        touching = false;
        var dx = e.changedTouches[0].clientX - touchStartX;
        var dy = e.changedTouches[0].clientY - touchStartY;
        if (Math.abs(dx) > 36 && Math.abs(dx) > Math.abs(dy)) {
          showImage(dx < 0 ? currentIndex + 1 : currentIndex - 1);
        }
      }, { passive: true });
    }

    var qtyInput = qs('[data-qv-qty-input]', body);
    var minus = qs('[data-qv-qty-minus]', body);
    var plus = qs('[data-qv-qty-plus]', body);
    if (minus) minus.addEventListener('click', function () {
      qtyInput.value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) - 1);
    });
    if (plus) plus.addEventListener('click', function () {
      qtyInput.value = (parseInt(qtyInput.value, 10) || 1) + 1;
    });

    var select = qs('[data-qv-variant]', body);
    var priceEl = qs('[data-qv-price]', body);
    var stockEl = qs('[data-qv-stock]', body);
    var addBtn = qs('[data-qv-add]', body);

    function currentVariant() {
      if (select) {
        var opt = select.options[select.selectedIndex];
        return {
          id: opt.value,
          price: parseInt(opt.getAttribute('data-price'), 10) || 0,
          compare_at_price: parseInt(opt.getAttribute('data-compare'), 10) || 0,
          available: opt.getAttribute('data-available') === 'true'
        };
      }
      return variants[0] || {};
    }

    if (select) {
      select.addEventListener('change', function () {
        var v = currentVariant();
        if (priceEl) priceEl.innerHTML = renderPrice(v);
        if (stockEl) stockEl.innerHTML = renderStock(v);
        if (addBtn) {
          addBtn.disabled = !v.available;
          addBtn.textContent = v.available ? 'Add to cart' : 'Sold out';
        }
      });
    }

    var form = qs('[data-qv-form]', body);
    var errorEl = qs('[data-qv-error]', body);
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var v = currentVariant();
        if (!v.id || !v.available || !window.BeepCart) return;
        if (errorEl) errorEl.hidden = true;
        addBtn.classList.add('is-loading');
        addBtn.disabled = true;
        var qty = parseInt(qtyInput.value, 10) || 1;
        window.BeepCart.add(v.id, qty).then(function () {
          addBtn.classList.remove('is-loading');
          addBtn.textContent = 'Added ✓';
          setTimeout(function () {
            addBtn.textContent = 'Add to cart';
            addBtn.disabled = false;
          }, 1400);
        }).catch(function (err) {
          addBtn.classList.remove('is-loading');
          addBtn.disabled = false;
          if (errorEl) { errorEl.textContent = err.message || 'Could not add to cart'; errorEl.hidden = false; }
        });
      });
    }
  }

  function open(handle) {
    if (!modal || !handle) return;
    isOpen = true;
    var myToken = ++requestToken;
    modal.hidden = false;
    body.innerHTML = '<div class="quick-view__loading"><span class="quick-view__spinner"></span></div>';
    void modal.offsetHeight;
    requestAnimationFrame(function () { modal.classList.add('is-open'); });
    lockScroll();

    fetch(rootPath() + 'products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('not found'); return r.json(); })
      .then(function (product) { if (isOpen && myToken === requestToken) renderProduct(product); })
      .catch(function () { if (isOpen && myToken === requestToken) renderError(); });
  }

  function init() {
    modal = qs('[data-quick-view]');
    if (!modal) return;
    body = qs('[data-quick-view-body]', modal);

    qsa('[data-quick-view-close]', modal).forEach(function (btn) { btn.addEventListener('click', close); });
    var backdrop = qs('[data-quick-view-backdrop]', modal);
    if (backdrop) backdrop.addEventListener('click', close);
    document.addEventListener('keydown', function (e) {
      if (!isOpen) return;
      if (e.key === 'Escape') { close(); return; }
      if (navigate && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        navigate(e.key === 'ArrowLeft' ? -1 : 1);
      }
    });

    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-quick-view-trigger]');
      if (!trigger) return;
      e.preventDefault();
      e.stopPropagation();
      open(trigger.getAttribute('data-quick-view-handle'));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  return { open: open, close: close };
})();
